<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <Header :title="$t('purchase_order_form')"
                        :isFilter="false"/>
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :title="$t('purchase_order_form')"
                              :isFilter="false"/>
            </template>

            <div v-if="requesterInfo">
                <!-- Requester Info Section -->
                <div class="row data-form">
                    <div class="col-12">
                        <div class="mb-4">
                            <h6 class="text-uppercase mb-0">{{ $t('information').toUpper() }}</h6>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                        <b-form-group :label="$t('full_name')">{{ requesterInfo.full_name }}</b-form-group>
                    </div>
                    <div class="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                        <b-form-group :label="$t('employee_id')">{{ requesterInfo.employee_id }}</b-form-group>
                    </div>
                    <div class="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                        <b-form-group :label="$t('job_title')">{{ requesterInfo.job_title_name }}</b-form-group>
                    </div>
                    <div class="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
                        <b-form-group :label="$t('budgetary_department')">{{ requesterInfo.budgetary_department_name }}</b-form-group>
                    </div>
                </div>

                <hr>
                <div class="mb-4">
                    <h6 class="text-uppercase mb-0">{{ $t('application_information').toUpper() }}</h6>
                </div>

                <ValidationObserver ref="purchaseOrderForm">
                    <b-row>
                        <!-- Notes -->
                        <b-col cols="12">
                            <ValidationProvider name="notes" rules="" v-slot="{errors}">
                                <b-form-group :label="$t('notes')">
                                    <b-form-textarea
                                        v-model="formData.notes"
                                        :class="errors[0] ? 'is-invalid' : ''"
                                        rows="3"
                                        maxlength="2000"
                                    />
                                    <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                </b-form-group>
                            </ValidationProvider>
                        </b-col>
                    </b-row>

                    <!-- Items Section -->
                    <hr>
                    <div class="mb-4">
                        <h6 class="text-uppercase mb-0">{{ $t('items').toUpper() }}</h6>
                    </div>

                    <div
                        v-for="(item, index) in formData.items"
                        :key="index"
                        class="mb-3 p-3 border rounded"
                    >
                        <b-row>
                            <!-- Type of Purchase -->
                            <b-col cols="12" md="3">
                                <ValidationProvider
                                    :name="'items.' + index + '.type_of_purchase'"
                                    rules="required"
                                    v-slot="{errors}"
                                >
                                    <b-form-group :label="$t('type_of_purchase')">
                                        <b-form-input
                                            v-model="item.type_of_purchase"
                                            :class="errors[0] ? 'is-invalid' : ''"
                                            maxlength="255"
                                        />
                                        <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>

                            <!-- Item Name -->
                            <b-col cols="12" md="3">
                                <ValidationProvider
                                    :name="'items.' + index + '.item_name'"
                                    rules="required"
                                    v-slot="{errors}"
                                >
                                    <b-form-group :label="$t('item_name')">
                                        <b-form-input
                                            v-model="item.item_name"
                                            :class="errors[0] ? 'is-invalid' : ''"
                                            maxlength="255"
                                        />
                                        <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>

                            <!-- Suggested Vendor -->
                            <b-col cols="12" md="3">
                                <ValidationProvider
                                    :name="'items.' + index + '.suggested_vendor'"
                                    rules=""
                                    v-slot="{errors}"
                                >
                                    <b-form-group :label="$t('suggested_vendor')">
                                        <b-form-input
                                            v-model="item.suggested_vendor"
                                            :class="errors[0] ? 'is-invalid' : ''"
                                            maxlength="255"
                                        />
                                        <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>

                            <!-- Quantity -->
                            <b-col cols="12" md="2">
                                <ValidationProvider
                                    :name="'items.' + index + '.quantity'"
                                    rules="required"
                                    v-slot="{errors}"
                                >
                                    <b-form-group :label="$t('quantity')">
                                        <b-form-input
                                            v-model="item.quantity"
                                            type="number"
                                            min="1"
                                            :class="errors[0] ? 'is-invalid' : ''"
                                        />
                                        <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"/>
                                    </b-form-group>
                                </ValidationProvider>
                            </b-col>

                            <!-- Remove Button -->
                            <b-col cols="12" md="1" class="d-flex align-items-center mt-2">
                                <b-button
                                    variant="danger"
                                    size="sm"
                                    :disabled="formData.items.length === 1"
                                    @click="removeItem(index)"
                                >
                                    <i class="ri-delete-bin-line"></i>
                                </b-button>
                            </b-col>
                        </b-row>
                    </div>

                    <b-button variant="outline-primary" size="sm" @click="addItem" class="mb-4">
                        <i class="ri-add-line"></i> {{ $t('add_item') }}
                    </b-button>
                </ValidationObserver>

                <div class="d-flex justify-content-center mt-3">
                    <b-form-group>
                        <b-button variant="primary" @click="save">{{ $t('save') }}</b-button>
                    </b-form-group>
                </div>
            </div>

            <!-- Not authorized / error message -->
            <div v-else>
                <div class="message-box" v-if="message && !showSuccessMessage">
                    <b-alert show variant="info" class="d-flex align-items-center">
                        <b-icon icon="info-circle"></b-icon>
                        <div class="alert-content ml-2">
                            <p class="mb-0 font-weight-bold">{{ $t('api.' + message) }}</p>
                        </div>
                    </b-alert>
                </div>
            </div>
        </app-layout>
    </div>
</template>

<script>
import AppLayout from '@/layouts/AppLayout';
import Header from '@/layouts/AppLayout/Header';
import HeaderMobile from '@/layouts/AppLayout/HeaderMobile';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import PurchaseOrderFormService from '@/services/PurchaseOrderFormService';

export default {
    components: {
        AppLayout,
        Header,
        HeaderMobile,
        ValidationObserver,
        ValidationProvider,
    },
    data() {
        return {
            formData: {
                notes: null,
                items: [
                    {
                        type_of_purchase: null,
                        item_name: null,
                        suggested_vendor: null,
                        quantity: 1,
                    },
                ],
            },
            requesterInfo: null,
            showSuccessMessage: false,
            message: null,
        };
    },
    metaInfo() {
        return {
            title: this.$t('purchase_order_form'),
        };
    },
    methods: {
        async getFormData() {
            try {
                const response = await PurchaseOrderFormService.getFormData();
                const data = response.data.data;
                this.requesterInfo = data;
            } catch (error) {
                this.message = error?.data?.message;
                this.showErrors(error);
            }
        },
        addItem() {
            this.formData.items.push({
                type_of_purchase: null,
                item_name: null,
                suggested_vendor: null,
                quantity: 1,
            });
        },
        removeItem(index) {
            this.formData.items.splice(index, 1);
        },
        async save() {
            if (!await this.$refs.purchaseOrderForm.validate()) return;
            try {
                const res = await PurchaseOrderFormService.store(this.formData);
                this.$toast.success(this.$t(res.data.message));
                this.$router.push('/my-staff-requests/1');
            } catch (error) {
                this.showErrors(error, this.$refs.purchaseOrderForm);
            }
        },
    },
    async created() {
        await this.getFormData();
    },
};
</script>
